import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
    Plus, Save, Trash2, ClipboardCheck, Loader2, Warehouse,
    Calendar, ChevronDown, ChevronRight, History, Package, Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WAREHOUSES = [
    { id: 'ana-depo', name: 'Ana Depo', description: 'Merkez depo alani', color: 'bg-blue-500' },
    { id: 'bar-depo', name: 'Bar Deposu', description: 'Icecek ve bar malzemeleri', color: 'bg-amber-500' },
    { id: 'mutfak-depo', name: 'Mutfak Deposu', description: 'Gida ve mutfak malzemeleri', color: 'bg-green-500' },
    { id: 'temizlik-depo', name: 'Temizlik Deposu', description: 'Temizlik malzemeleri', color: 'bg-purple-500' },
    { id: 'teknik-depo', name: 'Teknik Depo', description: 'Teknik malzeme ve yedek parcalar', color: 'bg-slate-500' },
];

const UNITS = [
    { value: 'adet', label: 'Adet' },
    { value: 'cl', label: 'CL' },
    { value: 'litre', label: 'Litre' },
    { value: 'koli', label: 'Koli' },
    { value: 'box', label: 'Box' },
    { value: 'paket', label: 'Paket' },
    { value: 'kg', label: 'KG' },
    { value: 'sarf', label: 'Sarf Malzemesi' },
    { value: 'diğer', label: 'Diğer' },
];

export function Sayim() {
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [items, setItems] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [activeIndex, setActiveIndex] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState([]);
    const [historyDialog, setHistoryDialog] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [existingCount, setExistingCount] = useState(null);
    const [notes, setNotes] = useState('');

    const getToken = () => localStorage.getItem('token');

    // Load existing count when warehouse or date changes
    const loadCount = useCallback(async () => {
        if (!selectedWarehouse) return;
        try {
            setLoading(true);
            const res = await fetch(
                `${API_URL}/api/warehouse-counts?warehouse_id=${selectedWarehouse}&date=${selectedDate}`,
                { headers: { 'Authorization': `Bearer ${getToken()}` } }
            );
            if (res.ok) {
                const data = await res.json();
                if (data.length > 0) {
                    const count = data[0];
                    setExistingCount(count);
                    setItems(count.items.map(i => ({ ...i, quantity: String(i.quantity) })));
                    setNotes(count.notes || '');
                } else {
                    setExistingCount(null);
                    setItems([{ product_name: '', unit: 'adet', quantity: '' }]);
                    setNotes('');
                }
            }
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [selectedWarehouse, selectedDate]);

    useEffect(() => { loadCount(); }, [loadCount]);

    const handleSearch = async (value, index) => {
        setActiveIndex(index);
        const newItems = [...items];
        newItems[index].product_name = value;
        setItems(newItems);
        if (value.length >= 1) {
            try {
                const res = await fetch(`${API_URL}/api/products/autocomplete?q=${encodeURIComponent(value)}`, {
                    headers: { 'Authorization': `Bearer ${getToken()}` }
                });
                if (res.ok) setSuggestions(await res.json());
                else setSuggestions([]);
            } catch { setSuggestions([]); }
        } else { setSuggestions([]); }
    };

    const selectProduct = (index, product) => {
        const newItems = [...items];
        newItems[index].product_name = product.name;
        newItems[index].unit = product.unit || 'adet';
        setItems(newItems);
        setSuggestions([]);
        setActiveIndex(null);
    };

    const updateItem = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        setItems(newItems);
    };

    const addRow = () => {
        setItems([...items, { product_name: '', unit: 'adet', quantity: '' }]);
    };

    const removeRow = (index) => {
        if (items.length <= 1) {
            setItems([{ product_name: '', unit: 'adet', quantity: '' }]);
            return;
        }
        setItems(items.filter((_, i) => i !== index));
    };

    const saveCount = async () => {
        const validItems = items.filter(i => i.product_name && parseFloat(i.quantity) > 0);
        if (validItems.length === 0) {
            toast.error('En az bir urun ve miktar giriniz');
            return;
        }
        try {
            setSaving(true);
            const res = await fetch(`${API_URL}/api/warehouse-counts`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    warehouse_id: selectedWarehouse,
                    date: selectedDate,
                    items: validItems.map(i => ({ ...i, quantity: parseFloat(i.quantity) })),
                    notes,
                })
            });
            if (res.ok) {
                const data = await res.json();
                setExistingCount(data);
                toast.success('Sayim basariyla kaydedildi');
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || 'Kayit hatasi');
            }
        } catch { toast.error('Kayit hatasi'); }
        finally { setSaving(false); }
    };

    const loadHistory = async (warehouseId) => {
        try {
            setHistoryLoading(true);
            const res = await fetch(`${API_URL}/api/warehouse-counts/history/${warehouseId}`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (res.ok) setHistory(await res.json());
        } catch (e) { console.error(e); }
        finally { setHistoryLoading(false); }
    };

    const deleteCount = async (id) => {
        if (!window.confirm('Bu sayimi silmek istediginize emin misiniz?')) return;
        try {
            const res = await fetch(`${API_URL}/api/warehouse-counts/${id}`, {
                method: 'DELETE', headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (res.ok) {
                toast.success('Sayim silindi');
                setHistory(prev => prev.filter(h => h.id !== id));
                if (existingCount?.id === id) {
                    setExistingCount(null);
                    setItems([{ product_name: '', unit: 'adet', quantity: '' }]);
                    setNotes('');
                }
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || 'Silme hatasi');
            }
        } catch { toast.error('Silme hatasi'); }
    };

    // Warehouse selection screen
    if (!selectedWarehouse) {
        return (
            <div className="space-y-6" data-testid="sayim-page">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-800">Sayim</h1>
                    <p className="text-slate-500 text-sm mt-1">Depo bazli stok sayim islemleri - Depo secin</p>
                </div>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {WAREHOUSES.map(wh => (
                        <Card key={wh.id}
                            className="hover:shadow-md transition-all cursor-pointer border-l-4 hover:scale-[1.02]"
                            style={{ borderLeftColor: 'var(--border)' }}
                            onClick={() => setSelectedWarehouse(wh.id)}
                            data-testid={`warehouse-${wh.id}`}>
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <div className={`w-8 h-8 rounded-lg ${wh.color} flex items-center justify-center`}>
                                            <Warehouse className="w-4 h-4 text-white" />
                                        </div>
                                        {wh.name}
                                    </CardTitle>
                                </div>
                                <p className="text-xs text-slate-500 mt-1">{wh.description}</p>
                            </CardHeader>
                            <CardContent className="pt-0">
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" className="text-xs"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            loadHistory(wh.id);
                                            setHistoryDialog(true);
                                        }}
                                        data-testid={`history-${wh.id}`}>
                                        <History className="w-3.5 h-3.5 mr-1" /> Gecmis
                                    </Button>
                                    <Button size="sm" className="text-xs bg-blue-600 hover:bg-blue-700"
                                        onClick={() => setSelectedWarehouse(wh.id)}>
                                        <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Sayim Yap
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* History Dialog */}
                <Dialog open={historyDialog} onOpenChange={setHistoryDialog}>
                    <DialogContent className="max-w-lg max-h-[80vh] overflow-auto">
                        <DialogHeader>
                            <DialogTitle>Sayim Gecmisi</DialogTitle>
                        </DialogHeader>
                        {historyLoading ? (
                            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
                        ) : history.length === 0 ? (
                            <p className="text-center text-slate-400 py-8">Gecmis sayim bulunamadi</p>
                        ) : (
                            <div className="space-y-3">
                                {history.map(h => (
                                    <div key={h.id} className="p-3 bg-slate-50 rounded-lg" data-testid={`history-item-${h.id}`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <Badge variant="outline">{h.date}</Badge>
                                                <span className="text-xs text-slate-400">{h.items?.length || 0} kalem</span>
                                            </div>
                                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400"
                                                onClick={() => deleteCount(h.id)}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                        <div className="flex flex-wrap gap-1">
                                            {h.items?.slice(0, 5).map((item, i) => (
                                                <Badge key={i} variant="secondary" className="text-xs">
                                                    {item.product_name}: {item.quantity} {item.unit}
                                                </Badge>
                                            ))}
                                            {(h.items?.length || 0) > 5 && (
                                                <Badge variant="secondary" className="text-xs">+{h.items.length - 5} daha</Badge>
                                            )}
                                        </div>
                                        {h.notes && <p className="text-xs text-slate-500 mt-1 italic">Not: {h.notes}</p>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    const warehouseInfo = WAREHOUSES.find(w => w.id === selectedWarehouse) || { name: selectedWarehouse, color: 'bg-slate-500' };

    // Counting screen
    return (
        <div className="space-y-6" data-testid="sayim-count-page">
            {/* Header with back button */}
            <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={() => setSelectedWarehouse(null)} data-testid="sayim-back-btn">
                    <ChevronRight className="w-4 h-4 rotate-180" />
                </Button>
                <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-800 flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg ${warehouseInfo.color} flex items-center justify-center`}>
                            <Warehouse className="w-4 h-4 text-white" />
                        </div>
                        {warehouseInfo.name} - Sayim
                    </h1>
                    <p className="text-slate-500 text-sm mt-1">
                        {existingCount ? 'Mevcut sayim guncelleniyor' : 'Yeni sayim'}
                    </p>
                </div>
            </div>

            {/* Date Selection */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
                                className="w-40 h-9" data-testid="sayim-date" />
                        </div>
                        {existingCount && (
                            <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                                Mevcut sayim var - {existingCount.items?.length || 0} kalem
                            </Badge>
                        )}
                        <div className="ml-auto flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => {
                                loadHistory(selectedWarehouse);
                                setHistoryDialog(true);
                            }}>
                                <History className="w-4 h-4 mr-1" /> Gecmis
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Items */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
            ) : (
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Package className="w-5 h-5 text-blue-500" />
                                Sayim Kalemleri ({items.filter(i => i.product_name && parseFloat(i.quantity) > 0).length} urun)
                            </CardTitle>
                            <Button variant="outline" size="sm" onClick={addRow}>
                                <Plus className="w-4 h-4 mr-1" /> Satir Ekle
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {/* Header */}
                        <div className="hidden md:flex items-center gap-2 text-xs text-slate-500 font-medium px-1">
                            <div className="flex-1">Urun Adi</div>
                            <div className="w-28">Birim</div>
                            <div className="w-24 text-center">Miktar</div>
                            <div className="w-8"></div>
                        </div>

                        {items.map((item, idx) => (
                            <div key={idx} className="flex flex-col md:flex-row items-start md:items-center gap-2 p-2 md:p-0 bg-slate-50 md:bg-transparent rounded-lg md:rounded-none">
                                {/* Product name with autocomplete */}
                                <div className="flex-1 relative w-full">
                                    <div className="relative">
                                        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                                        <Input
                                            value={item.product_name}
                                            onChange={(e) => handleSearch(e.target.value, idx)}
                                            onFocus={() => setActiveIndex(idx)}
                                            onBlur={() => setTimeout(() => { if (activeIndex === idx) setActiveIndex(null); }, 200)}
                                            placeholder="Urun adi yazin..."
                                            className="h-10 pl-8"
                                            data-testid={`sayim-product-${idx}`}
                                        />
                                    </div>
                                    {activeIndex === idx && suggestions.length > 0 && (
                                        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                                            {suggestions.map((p) => (
                                                <div key={p.id}
                                                    className="px-3 py-2.5 hover:bg-slate-100 cursor-pointer flex justify-between items-center"
                                                    onMouseDown={(e) => { e.preventDefault(); selectProduct(idx, p); }}>
                                                    <span className="text-sm font-medium">{p.name}</span>
                                                    <Badge variant="outline" className="text-xs uppercase">{p.unit}</Badge>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Unit selector */}
                                <Select value={item.unit} onValueChange={(v) => updateItem(idx, 'unit', v)}>
                                    <SelectTrigger className="w-full md:w-28 h-10" data-testid={`sayim-unit-${idx}`}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {UNITS.map(u => (
                                            <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>

                                {/* Quantity */}
                                <Input
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                                    placeholder="Miktar"
                                    className="w-full md:w-24 h-10 text-center"
                                    data-testid={`sayim-qty-${idx}`}
                                />

                                {/* Remove */}
                                <Button variant="ghost" size="icon" className="h-10 w-10 text-red-400 hover:text-red-600 flex-shrink-0"
                                    onClick={() => removeRow(idx)} data-testid={`sayim-remove-${idx}`}>
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* Notes */}
            <Card>
                <CardContent className="p-4">
                    <label className="text-sm font-medium text-slate-700">Notlar</label>
                    <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                        placeholder="Sayim notu ekleyin (opsiyonel)..."
                        className="mt-1 h-10" data-testid="sayim-notes" />
                </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
                <Button
                    onClick={saveCount}
                    disabled={saving}
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 px-8"
                    data-testid="save-count-btn"
                >
                    {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
                    {saving ? 'Kaydediliyor...' : (existingCount ? 'Sayimi Guncelle' : 'Sayimi Kaydet')}
                </Button>
            </div>

            {/* History Dialog */}
            <Dialog open={historyDialog} onOpenChange={setHistoryDialog}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-auto">
                    <DialogHeader>
                        <DialogTitle>{warehouseInfo.name} - Sayim Gecmisi</DialogTitle>
                    </DialogHeader>
                    {historyLoading ? (
                        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
                    ) : history.length === 0 ? (
                        <p className="text-center text-slate-400 py-8">Gecmis sayim bulunamadi</p>
                    ) : (
                        <div className="space-y-3">
                            {history.map(h => (
                                <div key={h.id} className="p-3 bg-slate-50 rounded-lg" data-testid={`history-item-${h.id}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline">{h.date}</Badge>
                                            <span className="text-xs text-slate-400">
                                                {h.items?.length || 0} kalem
                                                {h.created_by_name && ` - ${h.created_by_name}`}
                                            </span>
                                        </div>
                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400"
                                            onClick={() => deleteCount(h.id)}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                        {h.items?.slice(0, 5).map((item, i) => (
                                            <Badge key={i} variant="secondary" className="text-xs">
                                                {item.product_name}: {item.quantity} {item.unit}
                                            </Badge>
                                        ))}
                                        {(h.items?.length || 0) > 5 && (
                                            <Badge variant="secondary" className="text-xs">+{h.items.length - 5} daha</Badge>
                                        )}
                                    </div>
                                    {h.notes && <p className="text-xs text-slate-500 mt-1 italic">Not: {h.notes}</p>}
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default Sayim;
